import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useStore } from "../context/StoreContext";
import { money, imgUrl, STATUS_LABELS } from "../lib/shop";
import { Seo } from "../components/site/Seo";
import { destinationText } from "../components/site/DeliveryFields";

export function OrderSummary({ o }) {
  return (
    <div className="card-dark p-6 space-y-4" data-testid="order-summary">
      {o.items.map((l) => (
        <div key={l.product_id + (l.colour || "") + l.size} className="flex gap-3 text-sm">
          <img src={imgUrl(l.image)} alt="" className="w-14 h-14 rounded-lg object-cover" />
          <div className="flex-1 min-w-0"><p className="font-semibold truncate">{l.name}</p><p className="text-zinc-400 text-xs">{[l.colour, l.size].filter(Boolean).join(" · ")} × {l.qty} · {money(l.unit_price)}</p>{l.sku && <p className="text-[11px] text-zinc-500">SKU {l.sku}</p>}</div>
          <span className="font-bold">{money(l.line_total)}</span>
        </div>
      ))}
      <div className="border-t border-white/10 pt-4 text-sm space-y-2">
        <div className="flex justify-between"><span className="text-zinc-400">Subtotal</span><span>{money(o.subtotal)}</span></div>
        <div className="flex justify-between"><span className="text-zinc-400">Delivery ({o.delivery_zone?.name})</span><span>{money(o.delivery_fee)}</span></div>
        {o.discount > 0 && <div className="flex justify-between"><span className="text-zinc-400">Discount ({o.discount_code})</span><span>-{money(o.discount)}</span></div>}
        <div className="flex justify-between text-lg font-extrabold"><span>Total</span><span className="text-gold">{money(o.total)}</span></div>
      </div>
      <div className="text-xs text-zinc-400 border-t border-white/10 pt-4">
        {o.customer.first_name} {o.customer.last_name} · {destinationText(o.customer, o.delivery_zone?.method)}{o.delivery_zone?.eta ? ` · ${o.delivery_zone.eta}` : ""}
      </div>
    </div>
  );
}

export default function OrderConfirmation() {
  const { number } = useParams();
  const [params] = useSearchParams();
  const token = params.get("t");
  const { clearCart } = useStore();
  const [o, setO] = useState(null);
  const [err, setErr] = useState("");
  const tries = useRef(0);

  useEffect(() => {
    let timer;
    const load = async () => {
      try {
        const r = await api.get(`/orders/view/${number}`, { params: { t: token } });
        setO(r.data);
        if (r.data.payment_status === "paid") clearCart();
        if (r.data.payment_status === "pending" && tries.current++ < 15) timer = setTimeout(load, 4000);
      } catch (e) { setErr(errMsg(e)); }
    };
    load();
    return () => clearTimeout(timer);
  }, [number, token, clearCart]);

  if (err) return <div className="container-x py-24"><h1 className="font-display text-5xl">Order not found</h1><p className="text-zinc-400 mt-2">{err}</p></div>;
  if (!o) return <div className="container-x py-24 text-zinc-400">Checking your payment...</div>;
  const paid = o.payment_status === "paid";
  const pending = o.payment_status === "pending";
  const Icon = paid ? CheckCircle2 : pending ? Clock : XCircle;
  return (
    <div className="container-x py-12 sm:py-20 grid lg:grid-cols-2 gap-10">
      <Seo title="Order Confirmation" />
      <div>
        <Icon size={56} className={paid ? "text-gold" : pending ? "text-amber-300" : "text-red-400"} />
        <p className="eyebrow mt-6">Order number</p>
        <p className="font-display text-6xl sm:text-7xl text-gold-gradient" data-testid="confirmation-order-number">{o.order_number}</p>
        <h1 className="font-display text-4xl sm:text-5xl mt-4" data-testid="confirmation-heading">
          {paid ? "Thank you! Payment confirmed." : pending ? "Verifying your payment..." : `Payment ${o.payment_status}`}
        </h1>
        <p className="text-zinc-400 mt-3 max-w-md" data-testid="confirmation-message">
          {paid ? `Status: ${STATUS_LABELS[o.status]}. We'll prepare your order for courier delivery. Keep your order number to track it.`
            : pending ? "We're confirming your payment with iKhokha. This page updates automatically — please don't pay twice."
              : "Your payment was not completed and you have not been charged by us. Your cart is still saved — you can try again."}
        </p>
        {o.payment?.reference && <p className="text-xs text-zinc-500 mt-3">Payment reference: {o.payment.reference}</p>}
        <div className="flex flex-col sm:flex-row gap-3 mt-8">
          {paid ? <Link to="/track-order" className="btn-gold" data-testid="confirmation-track-button">Track order</Link>
            : !pending && <Link to="/checkout" className="btn-gold" data-testid="confirmation-retry-button">Try again</Link>}
          <Link to="/shop" className="btn-outline">Continue shopping</Link>
        </div>
      </div>
      <OrderSummary o={o} />
    </div>
  );
}

export function PaymentDemo() {
  const { number } = useParams();
  const [params] = useSearchParams();
  const token = params.get("t");
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const pay = async (outcome) => {
    setBusy(true);
    try {
      await api.post(`/payments/demo/${number}`, { token, outcome });
      nav(`/order-confirmation/${number}?t=${token}`);
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <div className="container-x py-20 max-w-xl">
      <Seo title="Demo payment" />
      <div className="card-dark p-8 border-amber-400/40" data-testid="demo-payment-page">
        <p className="text-xs font-extrabold tracking-widest text-amber-300">MOCKED PAYMENT — DEMO MODE</p>
        <h1 className="font-display text-5xl mt-3">iKhokha payment simulator</h1>
        <p className="text-sm text-zinc-400 mt-3">Real iKhokha credentials have not been added yet, so this simulator stands in for the iKhokha payment page. The server records the result — nothing is marked paid from the browser alone in live mode.</p>
        <p className="mt-6 text-sm">Order <b>{number}</b></p>
        <div className="grid gap-3 mt-6">
          <button disabled={busy} onClick={() => pay("paid")} className="btn-gold" data-testid="demo-pay-success">Simulate successful payment</button>
          <button disabled={busy} onClick={() => pay("failed")} className="btn-outline" data-testid="demo-pay-failed">Simulate failed payment</button>
          <button disabled={busy} onClick={() => pay("cancelled")} className="btn-outline" data-testid="demo-pay-cancelled">Cancel payment</button>
        </div>
      </div>
    </div>
  );
}
